import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/components/AuthProvider";
import { checkPaymentExpiration } from "@/utils/paymentExpiration";

export function useTrialProtection() {
  const { empresa } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isTrialExpired, setIsTrialExpired] = useState(false);
  const [blockReason, setBlockReason] = useState<"trial" | "subscription" | null>(null);

  useEffect(() => {
    // Aguardar carregamento dos dados antes de verificar trial
    if (!empresa) {
      console.log("⏳ Aguardando carregamento dos dados da empresa...");
      return;
    }

    // Se é premium mas não tem trial_end_date, ainda deve permitir acesso
    // Não bloquear premium users por falta de trial_end_date
    if (!empresa?.trial_end_date && !empresa?.is_premium) {
      console.log("⏳ Trial end date não encontrado - aguardando carregamento completo...");
      // Não definir como expirado imediatamente, aguardar carregamento
      return;
    }

    console.log("🔍 Dados da empresa recebidos:", {
      id: empresa.id,
      nome: empresa.nome,
      is_premium: empresa.is_premium,
      status: empresa.status,
      trial_end_date: empresa.trial_end_date
    });

    // Verificar expiração de pagamento (inclui verificação de premium e trial)
    const checkExpiration = async () => {
      try {
        const paymentStatus = await checkPaymentExpiration(empresa.id);
        
        console.log("🔍 Status de pagamento:", {
          isPremium: paymentStatus.isPremium,
          isExpired: paymentStatus.isExpired,
          daysRemaining: paymentStatus.daysRemaining,
          planType: paymentStatus.planType,
          shouldBlockAccess: paymentStatus.shouldBlockAccess,
          currentPath: location.pathname
        });

        setIsTrialExpired(paymentStatus.shouldBlockAccess);
        if (paymentStatus.shouldBlockAccess) {
          setBlockReason(paymentStatus.planType === "subscription_expired" ? "subscription" : "trial");
        } else {
          setBlockReason(null);
        }

        // Se deve bloquear acesso e não está na página de assinatura, redirecionar
        if (paymentStatus.shouldBlockAccess && 
            location.pathname !== "/assinatura" && 
            location.pathname !== "/minha-conta" &&
            location.pathname !== "/verificar-pagamento" &&
            location.pathname !== "/ajuda" &&
            location.pathname !== "/documentacao" &&
            location.pathname !== "/faq") {
          console.log("🚫 Acesso bloqueado - redirecionando para assinatura");
          navigate("/assinatura", { replace: true });
        }

        // Se está próximo do vencimento, mostrar aviso
        if (paymentStatus.nextPaymentDue && paymentStatus.daysRemaining <= 3) {
          console.log(`⚠️ Pagamento vence em ${paymentStatus.daysRemaining} dias`);
        }

      } catch (error) {
        console.error("❌ Erro ao verificar expiração de pagamento:", error);
        // Em caso de erro, bloquear acesso por segurança
        setIsTrialExpired(true);
        setBlockReason("trial");
        if (location.pathname !== "/assinatura" && location.pathname !== "/minha-conta") {
          navigate("/assinatura", { replace: true });
        }
      }
    };

    checkExpiration();
  }, [empresa?.id, location.pathname, navigate, empresa]);

  return {
    isTrialExpired,
    blockReason,
    trialEndDate: empresa?.trial_end_date,
    daysRemaining: empresa?.trial_end_date 
      ? Math.ceil((new Date(empresa.trial_end_date).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))
      : null, // Retornar null em vez de 7 para indicar que não temos dados
    isLoading: !empresa || !empresa.trial_end_date, // Adicionar flag de carregamento
    isPremium: empresa?.is_premium || false,
    planType: ((empresa as any)?.plan_type as string) || null
  };
}
